import { typographyTokens } from '../../theme/typographyTokens';
import { newUiColorTokens } from '../../theme/themeTokens';
import React, { useEffect, useRef, useState } from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { Box, Grid, Divider, Typography } from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import {
  DataFieldUpdate,
  DataFieldWithOptionsAndLocations,
} from '../../../shared/types/DataField';
import DataForm from '../../features/Knockout/DataForm';
import Button from '../Button';
import Checkbox from '../Checkbox';
import Toolbar from '../Toolbar';
import { TaskExtended } from '../../../shared/types/Task';
import { useUpdateTasksMutation, useGetTaskTemplatesQuery } from '../../services/tasksSlice/tasksSlice';
import ModalWidget from '../ModalWidget/ModalWidget';
import StepPage from '../CreateTask/CreateTask';
import { IconCell } from '../TaskOverview/TaskHeader';
import UserDisplay from '../UserDisplay/UserDisplay';
import UserSearchBar from '../TaskOverview/AutoComplete';
import { User } from '../../../shared/types/User';
import {
  useGetDefaultStakeholdersForSEPQuery,
  useSetDefaultStakeholderForSEPDepartmentMutation,
} from '../../services/departmentSlice/departmentSlice';
import { useGetDepartmentsQuery } from '../../services/departmentSlice/departmentSlice';
import { Department } from '../../../shared/types/Department';
import { useGetSepQuery } from '../../services/sepSlice';
import { DefaultStakeholder } from '../../../shared/types/DefaultStakeholder';
import { ActionButtons } from '../CancelSave';
import { SEPPhase } from '../../../shared/types/SEP';
import TaskListTable from '../Tables/TaskListTable';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: string;
  currentSubTab: string;
}

interface VerticalTabsProps {
  currentSubTab: string;
  data: DataFieldUpdate[];
  setData: React.Dispatch<React.SetStateAction<DataFieldUpdate[]>>;
  dataFieldsForDepartment: DataFieldWithOptionsAndLocations[];
  handleSubTabChange: (event: React.SyntheticEvent, newValue: string) => void;
  handleCancel: () => void;
  handleSave: () => void;
  locationID?: number;
  tasksForDepartment: TaskExtended[];
  sepID: number;
  filterTasks: boolean;
  handleTasksSwitch: () => void;
  setDataChanged?: React.Dispatch<React.SetStateAction<boolean>>;
  dataChanged?: boolean;
  currentDepartmentID: number | undefined;
  onNewTaskAdded: () => void;
  buttonValidation?: boolean;
  hidePendingDeptTasks?: boolean;
  hideFilterChange?: () => void
  sortData?: any
  refetchTasks?: () => void;
  allSepTasks?: any[];
  isAdminUser?: boolean;
}

const StyledTab = styled((props: any) => <Tab disableRipple {...props} />)(
  ({ theme }) => ({
    textTransform: 'none',
    font: 'normal normal normal 16px/22px Open Sans;',
    height: '48px !important',
    minHeight: '48px',
    padding: '10px 10px 10px 0px',
    width: '100%',
    alignSelf: 'start',
    '&.Mui-selected': {
      backgroundColor: theme.palette.blue.lightBlue1,
    },
    '&.Mui-focusVisible': {
      backgroundColor: 'rgba(100, 95, 228, 0.32)',
    },
  })
);

function TabPanel(props: TabPanelProps) {
  const { children, value, index, currentSubTab, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== currentSubTab}
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      {...other}
      style={{ width: '100%' }}
    >
      {value === currentSubTab && <Box sx={{ px: 0 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  };
}

export default function VerticalTabs({
  currentSubTab,
  data,
  dataFieldsForDepartment,
  sortData,
  setData,
  handleCancel,
  handleSubTabChange,
  handleSave,
  locationID,
  tasksForDepartment,
  sepID,
  filterTasks,
  handleTasksSwitch,
  setDataChanged,
  dataChanged,
  currentDepartmentID,
  onNewTaskAdded,
  buttonValidation,
  hidePendingDeptTasks,
  hideFilterChange,
  refetchTasks,
  allSepTasks,
  isAdminUser
}: VerticalTabsProps) {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isStakeholderEdit, setIsStakeholderEdit] = useState<boolean>(false);
  const stakeholderRef = useRef(null);
  const { data: defaultStakeholders, refetch } =
    useGetDefaultStakeholdersForSEPQuery(sepID);
  const { data: sep } = useGetSepQuery(sepID);
  const isComplete = sep?.phase === SEPPhase.complete;

  const defaultStakeholder = React.useMemo(() => {
    return (
      defaultStakeholders?.find(
        (d: DefaultStakeholder) => d.departmentID === currentDepartmentID
      ) || null
    );
  }, [defaultStakeholders, currentDepartmentID]);
  const { data: departments, refetch: getDepts } = useGetDepartmentsQuery();
  const { data: taskTemplates } = useGetTaskTemplatesQuery();

  const sortedTasks = React.useMemo(() => {
    const updatedTasks = tasksForDepartment?.map((task: any) => {
      const updatedTask = { ...task };
      taskTemplates?.forEach((newTask: any) => {
        if (updatedTask.taskTemplateID === newTask.id) {
          updatedTask.groupId = newTask.groupId;
          updatedTask.groupName = newTask.groupName; 
        }
      });
      return updatedTask;
    }) || [];

    return [...updatedTasks].sort((a, b) => {
      const groupIdA = a.groupId !== null ? a.groupId : Infinity;
      const groupIdB = b.groupId !== null ? b.groupId : Infinity;
      return groupIdA - groupIdB; 
    });
  }, [tasksForDepartment, taskTemplates]);

  const department = React.useMemo(() => {
    return departments?.find((d: Department) => d.id === currentDepartmentID);
  }, [departments, currentDepartmentID, defaultStakeholders]);
  const [updateDefaultStakeholder] =
    useSetDefaultStakeholderForSEPDepartmentMutation();
  const handleDefaultStakeholderChange = async (user: User) => {
    setIsStakeholderEdit(false);
    await updateDefaultStakeholder({
      userID: user.id,
      sepID,
      departmentID: currentDepartmentID as number,
      stakeholderName: user.displayName || user.givenName || user.email || 'Stakeholder',
    });
  };
  /** Whether the department existed at the time this SEP was created.
   * If the department did not exist at the time of SEP creation, all controls are disabled.
   * Exception: SEP Process Supervisor department is always allowed. */
  const isSEPProcessSupervisor = department?.name === 'SEP Process Supervisor';
  const departmentExisted =
    department && sep && (department?.createdAt < sep?.createdAt || isSEPProcessSupervisor);
  return departmentExisted ? (
    <>
      <ModalWidget
        isOpen={isOpen}
        title="Add Task"
        children={
          <StepPage
            isInit={!isOpen}
            sepID={sepID}
            departmentID={currentDepartmentID}
            onFinish={(newTask: TaskExtended) => {
              onNewTaskAdded();
              setIsOpen(false);
            }}
          />
        }
        onClose={() => setIsOpen(false)}
      />
      <Box sx={{ marginLeft: 5, marginBottom: 5 }}>
        <Typography fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1">
          Default Stakeholder for {department?.name} for this SEP &nbsp; &nbsp;
          <IconCell
            icon="fa-regular fa-pencil"
            iconHover="fa-solid fa-pencil"
            onClick={() => setIsStakeholderEdit(true)}
          />
        </Typography>
        <Box ref={stakeholderRef} display="flex" alignItems="center">
          <UserDisplay
            userID={defaultStakeholder?.userID}
            user={defaultStakeholder?.user}
            showPicture
            showName
            title={`${department?.name}`}
          />
        </Box>
        <UserSearchBar
          type="stakeholder"
          show={isStakeholderEdit}
          anchor={stakeholderRef.current}
          adAppRoleName={department?.adAppRole}
          onClose={() => {
            setIsStakeholderEdit(false);
          }}
          onChange={(user: User) => handleDefaultStakeholderChange(user)}
        />
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          width: '100%',
          bgcolor: 'background.paper',
          display: 'flex',
          overflow: 'auto',
        }}
      >
        <Tabs
          orientation="vertical"
          variant="scrollable"
          value={currentSubTab}
          onChange={handleSubTabChange}
          aria-label="Vertical tabs"
          sx={{ borderRight: 1, borderColor: 'divider', minWidth: 182 }}
          TabIndicatorProps={{
            style: { backgroundColor: theme.palette.blue.lightBlue1 },
          }}
        >
          <StyledTab
            value="DepartmentReview"
            icon={<i className="fa-solid fa-book-open"></i>}
            iconPosition="start"
            label="Review"
            {...a11yProps(0)}
          />
          {/* 
            Deparment tab disabled

          <StyledTab
            value="Comments"
            icon={<i className="fa-solid fa-comments"></i>}
            iconPosition="start"
            label="Comments"
            {...a11yProps(1)}
            sx={{ paddingLeft: 3.4 }}
          /> */}
          <StyledTab
            value="Department"
            icon={<i className="fa-regular fa-list"></i>}
            iconPosition="start"
            label="Details"
            {...a11yProps(2)}
          />
        </Tabs>
        <Box sx={{ flexGrow: 1, minWidth: 0, overflowX: 'scroll' }}>
        <TabPanel value="Department" currentSubTab={currentSubTab} index={0}>
          <Box px={3}>
            <DataForm
              dataFields={sortData}
              data={data}
              setData={setData}
              locationID={locationID}
              setDataChanged={setDataChanged}
              currentSubTab={currentSubTab}
              currentDepartmentID={currentDepartmentID}
              sepData={sep}
            />
          </Box>
          {dataChanged && (
            <ActionButtons
              handleCancel={handleCancel}
              handleSave={handleSave}
              buttonValidation={buttonValidation}
            />
          )}
        </TabPanel>
        {/* <TabPanel value="Comments" currentSubTab={currentSubTab} index={1}>
          <Comments
            sepID={sepID}
            comments={
              comments?.filter(
                (comment: CommentExtended) =>
                  comment.departmentID === currentDepartmentID &&
                  !comment.taskID
              ) || []
            }
            currentDepartmentID={currentDepartmentID}
          />
        </TabPanel> */}
        <TabPanel
          value="DepartmentReview"
          currentSubTab={currentSubTab}
          index={2}
        >
          <Box px={3}>
            <DataForm
              dataFields={dataFieldsForDepartment}
              data={data}
              setData={setData}
              setDataChanged={setDataChanged}
              currentSubTab={currentSubTab}
              currentDepartmentID={currentDepartmentID}
            />
            {dataChanged && (
              <ActionButtons
                handleCancel={handleCancel}
                handleSave={handleSave}
                buttonValidation={buttonValidation}
              />
            )}
          </Box>
          <br />
          {tasksForDepartment.length > 0 && (
            <>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '10px',
                }}
              >
                <Toolbar
                  title="Task Assigner"
                  caption="Choose tasks to assign to complete the review process"
                  hideToggle
                />
                {(!isComplete || isAdminUser) && (
                <Button
                  variant="outlined"
                  size="small"
                  sx={{
                    '&.MuiButton-outlined': {
                      border: `2px solid ${newUiColorTokens.prime.blue}`,
                      color: newUiColorTokens.prime.blue,
                      textTransform: 'none',
                      fontWeight: typographyTokens.fontWeights.bold,
                      marginRight: '20px',
                      whiteSpace: 'nowrap',
                      flexShrink: 0,
                    },
                  }}
                  onClick={() => setIsOpen(true)}
                >
                  + Create New Task
                </Button>
                )}
              </Box>
              <Divider />
              <br />
              <Box px={3}>
                <TaskAssigner
                  sepId={sepID}
                  tasksForDepartment={sortedTasks}
                  refetch = {refetch}
                  getDepts = {getDepts}
                  taskTemplates={taskTemplates}
                  refetchTasks={refetchTasks}
                  allSepTasks={allSepTasks}
                />
                <br />
              </Box>
              <br />
              <Toolbar
                title="SEP Task History"
                caption="This is a list of all tasks that have been assigned up to this point for review"
                filter={filterTasks}
                onChange={handleTasksSwitch}
                hidePendingTasks={hidePendingDeptTasks}
                hideFilterChange={hideFilterChange}
              />
              <Divider />
              <Box sx={{ width: '100%' }}>
              <TaskListTable
                sepID={sepID}
                tasks={tasksForDepartment}
                sepPhase={sep?.phase}
              />
             </Box>
            </>
          )}
        </TabPanel>
        </Box>
      </Box>
    </>
  ) : (
    <Typography sx={{ marginLeft: 5 }} fontSize={typographyTokens.fontSizes.caption} color="gray.darkGray1">
      This department did not exist at the time this SEP was created.
    </Typography>
  );
}

const TaskAssigner = ({ tasksForDepartment, sepId, refetch, getDepts, taskTemplates, refetchTasks, allSepTasks }: any) => {
  const [updateTasks, updateMutationStatus] = useUpdateTasksMutation();
  const [TasksForDepartment, setTasksForDepartment] = useState<any[]>(tasksForDepartment || []);

  const handleCheck = (e: any, id: number) => {
    const isChecked = e.target.checked;
    const taskId = Number(id); // Ensure it's a number
    const currentTask = TasksForDepartment?.find((task: any) => Number(task.id) === taskId);
    
    if (!currentTask) return;
    
    // Get groupName from task or look it up from templates
    const currentTemplate = taskTemplates?.find((t: any) => t.id === currentTask.taskTemplateID);
    const groupName = currentTask.groupName || currentTemplate?.groupName;
    const isCurrentTaskMultidept = currentTemplate?.isMultiDepartment === true;
    
    // Check if ANY template in this group is multidept
    const hasMultideptInGroup = groupName && taskTemplates?.some((template: any) => {
      return template.groupName === groupName && template.isMultiDepartment === true;
    });
    
    let updatedTasks;
    
    if (hasMultideptInGroup && isCurrentTaskMultidept && isChecked) {
      // ENABLING Task A (multidept): Toggle Task A and also enable Task B/C in same dept
      // This gives immediate visual feedback
      updatedTasks = TasksForDepartment?.map((task: any) => {
        const taskTemplate = taskTemplates?.find((t: any) => t.id === task.taskTemplateID);
        const taskGroupName = task.groupName || taskTemplate?.groupName;
        
        if (Number(task.id) === taskId) {
          // This is the clicked Task A
          return { ...task, enabled: true };
        } else if (taskGroupName === groupName && !taskTemplate?.isMultiDepartment) {
          // This is Task B or C in same group - enable with Task A
          return { ...task, enabled: true };
        }
        return task;
      });
    } else if (hasMultideptInGroup && isCurrentTaskMultidept && !isChecked) {
      // DISABLING Task A (multidept): Check if any other Task A copy is enabled in other depts
      // Use allSepTasks to check across all departments
      const otherTaskACopyEnabled = allSepTasks?.some((task: any) => {
        // Must be same template (Task A), different task ID, and currently enabled
        return task.taskTemplateID === currentTask.taskTemplateID && 
               Number(task.id) !== taskId && 
               task.enabled === true;
      });
      
      if (otherTaskACopyEnabled) {
        // Another Task A copy is enabled - only uncheck this Task A, leave B/C checked
        updatedTasks = TasksForDepartment?.map((task: any) => {
          if (Number(task.id) === taskId) {
            return { ...task, enabled: false };
          }
          return task;
        });
      } else {
        // This is the last enabled Task A copy - uncheck A, B, C immediately
        updatedTasks = TasksForDepartment?.map((task: any) => {
          const taskTemplate = taskTemplates?.find((t: any) => t.id === task.taskTemplateID);
          const taskGroupName = task.groupName || taskTemplate?.groupName;
          
          if (Number(task.id) === taskId) {
            // This is the clicked Task A
            return { ...task, enabled: false };
          } else if (taskGroupName === groupName && !taskTemplate?.isMultiDepartment) {
            // This is Task B or C in same group - disable with Task A
            return { ...task, enabled: false };
          }
          return task;
        });
      }
    } else if (hasMultideptInGroup && !isCurrentTaskMultidept) {
      // NON-MULTIDEPT TASK (Task B/C) in multidept group clicked: Only toggle this task
      updatedTasks = TasksForDepartment?.map((task: any) => {
        if (Number(task.id) === taskId) {
          return { ...task, enabled: isChecked };
        }
        return task;
      });
    } else if (groupName) {
      // NORMAL GROUP with groupName: Toggle all tasks in group together
      updatedTasks = TasksForDepartment?.map((task: any) => {
        const taskTemplate = taskTemplates?.find((t: any) => t.id === task.taskTemplateID);
        const taskGroupName = task.groupName || taskTemplate?.groupName;
        if (taskGroupName === groupName) {
          return { ...task, enabled: isChecked };
        }
        return task;
      });
    } else {
      // Single task without group: Toggle only this task
      updatedTasks = TasksForDepartment?.map((task: any) => {
        if (Number(task.id) === taskId) {
          return { ...task, enabled: isChecked };
        }
        return task;
      });
    }
    
    if (updatedTasks) {
      setTasksForDepartment([...updatedTasks]); // Force new array reference
    }
  };

  const handleTasksCancel = () => {
    setTasksForDepartment(tasksForDepartment);
  };

  const handleTasksUpdate = async () => {
    // Only send updates for tasks that have been changed
    const simplifiedTasks = TasksForDepartment.map(cleaner).filter(
      (a, idx) => a.enabled !== tasksForDepartment[idx].enabled
    );
    
    await updateTasks({ sepId: sepId, tasks: simplifiedTasks });
    
    // Refetch tasks to get updated Task B/C states after backend processes multidept logic
    if (refetchTasks) {
      await refetchTasks();
    }
    await refetch();
    getDepts();
  };

  const cleaner = (obj: any) => {
    const { id, enabled,defaultAssignee, taskTemplateID, groupName, sepID } = obj;
    return { id, enabled, defaultAssignee, taskTemplateID, groupName, sepID  };
  };

  const compareArrays = (a: any[], b: any[]) =>
    a.length === b.length &&
    a.every((element, index) => element.enabled === b[index].enabled);

  const displayActionButtons = !compareArrays(
    tasksForDepartment,
    TasksForDepartment
  );

  // Sync from props when prop data changes
  useEffect(() => {
    if (tasksForDepartment?.length) {
      setTasksForDepartment(tasksForDepartment);
    }
  }, [tasksForDepartment]);

  return (
    <>
      <Grid container spacing={2}>
        {TasksForDepartment?.map((task: any, i: number) => {
          return (
            <Grid key={task.id} item md={4}>
              <Checkbox
                checked={task.enabled}
                onChange={(e: any) => handleCheck(e, task.id)}
                label={task.shortName}
              />
            </Grid>
          );
        })}
      </Grid>
      {displayActionButtons && (
        <ActionButtons
          loading={updateMutationStatus.isLoading}
          handleCancel={handleTasksCancel}
          handleSave={handleTasksUpdate}
        />
      )}
    </>
  );
};
